import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import "./App.css";

const API_BASE = process.env.REACT_APP_API_URL || "";

const MODELS = ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo", "o1-mini"];

const DEFAULT_SYSTEM = "You are a helpful, knowledgeable AI assistant. Be concise but thorough.";

function TypingDots() {
  return (
    <div className="typing-dots">
      <span /><span /><span />
    </div>
  );
}

function CodeBlock({ children, className }) {
  const lang = className?.replace("language-", "") || "text";
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="code-block">
      <div className="code-header">
        <span className="code-lang">{lang}</span>
        <button className="copy-btn" onClick={copy}>{copied ? "✓ Copied" : "Copy"}</button>
      </div>
      <SyntaxHighlighter style={oneDark} language={lang} PreTag="div">
        {String(children).trimEnd()}
      </SyntaxHighlighter>
    </div>
  );
}

export default function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState(localStorage.getItem("openai_key") || "");
  const [model, setModel] = useState("gpt-4o");
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM);
  const [temperature, setTemperature] = useState(0.7);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState(null);
  const [streamBuffer, setStreamBuffer] = useState("");
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamBuffer]);

  const saveKey = (k) => {
    setApiKey(k);
    localStorage.setItem("openai_key", k);
  };

  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading) return;
    if (!apiKey) { setError("Enter your OpenAI API key in Settings."); return; }

    const userMsg = { role: "user", content: input.trim() };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");
    setLoading(true);
    setError(null);
    setStreamBuffer("");

    try {
      const res = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": apiKey,
        },
        body: JSON.stringify({
          messages: history,
          model,
          temperature,
          max_tokens: 2048,
          system_prompt: systemPrompt,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.content) {
              full += parsed.content;
              setStreamBuffer(full);
            }
          } catch {}
        }
      }

      setMessages(prev => [...prev, { role: "assistant", content: full }]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setStreamBuffer("");
    }
  }, [input, loading, messages, apiKey, model, temperature, systemPrompt]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => { setMessages([]); setError(null); };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <div className="logo">
            <div className="logo-icon">⬡</div>
            <span className="logo-text">AI Gateway</span>
          </div>
          <div className="model-badge">{model}</div>
        </div>
        <div className="header-right">
          {messages.length > 0 && (
            <button className="icon-btn" onClick={clearChat} title="Clear chat">↺</button>
          )}
          <button className="icon-btn settings-btn" onClick={() => setShowSettings(s => !s)}>
            ⚙
          </button>
        </div>
      </header>

      {/* Settings panel */}
      {showSettings && (
        <div className="settings-panel">
          <div className="settings-grid">
            <label>
              <span>OpenAI API Key</span>
              <input
                type="password"
                value={apiKey}
                onChange={e => saveKey(e.target.value)}
                placeholder="sk-..."
                className="settings-input"
              />
            </label>
            <label>
              <span>Model</span>
              <select value={model} onChange={e => setModel(e.target.value)} className="settings-input">
                {MODELS.map(m => <option key={m}>{m}</option>)}
              </select>
            </label>
            <label>
              <span>Temperature: {temperature}</span>
              <input
                type="range" min="0" max="2" step="0.1"
                value={temperature}
                onChange={e => setTemperature(parseFloat(e.target.value))}
              />
            </label>
            <label className="full-width">
              <span>System Prompt</span>
              <textarea
                value={systemPrompt}
                onChange={e => setSystemPrompt(e.target.value)}
                rows={3}
                className="settings-input"
              />
            </label>
          </div>
        </div>
      )}

      {/* Messages */}
      <main className="chat-area">
        {messages.length === 0 && !loading && (
          <div className="empty-state">
            <div className="empty-icon">⬡</div>
            <h2>AI Gateway</h2>
            <p>Powered by OpenAI — your private AI interface</p>
            <div className="suggestions">
              {["Explain quantum computing simply", "Write a Python web scraper", "What's the best way to learn Rust?"].map(s => (
                <button key={s} className="suggestion" onClick={() => setInput(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`message ${m.role}`}>
            <div className="msg-avatar">{m.role === "user" ? "U" : "⬡"}</div>
            <div className="msg-content">
              {m.role === "assistant" ? (
                <ReactMarkdown
                  components={{
                    code({ inline, className, children }) {
                      return inline
                        ? <code className="inline-code">{children}</code>
                        : <CodeBlock className={className}>{children}</CodeBlock>;
                    }
                  }}
                >
                  {m.content}
                </ReactMarkdown>
              ) : (
                <p>{m.content}</p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="message assistant">
            <div className="msg-avatar">⬡</div>
            <div className="msg-content">
              {streamBuffer ? (
                <ReactMarkdown>{streamBuffer}</ReactMarkdown>
              ) : (
                <TypingDots />
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="error-banner">⚠ {error}</div>
        )}

        <div ref={bottomRef} />
      </main>

      {/* Input */}
      <footer className="input-area">
        <div className="input-box">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Message AI Gateway… (Enter to send, Shift+Enter for newline)"
            rows={1}
            className="chat-input"
          />
          <button
            className={`send-btn ${loading || !input.trim() ? "disabled" : ""}`}
            onClick={sendMessage}
            disabled={loading || !input.trim()}
          >
            {loading ? "…" : "↑"}
          </button>
        </div>
        <p className="input-hint">AI Gateway — self-hosted OpenAI proxy</p>
      </footer>
    </div>
  );
}
